import {
    Contract,
    ContractCreateDto,
    ContractListItem,
    ContractListItemMinDto,
    ContractTemplateDto,
    ContractUpdateDto,
} from 'types/contracts';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';
import { filteredAndSortedQuery } from './util/filteredAndSortedQuery';

const APIContractsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Contract', 'ContractsListItem', 'ContractsCount', 'Notifications'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getContractById: builder.query<Contract, string>({
                query: (entityId) => `/v1/contracts/${entityId}`,
                keepUnusedDataFor: 0,
                providesTags: (_result, _error, entityId) => [{ type: 'Contract', id: entityId }],
            }),
            getContractsList: accumulatedQueryWithFilters<ContractListItemMinDto>(builder, '/v1/contracts/min', {
                providesTags: ['ContractsListItem'],
            }),
            getContractsWithFilters: accumulatedQueryWithFilters<ContractListItem>(builder, '/v1/contracts', {
                providesTags: ['ContractsListItem'],
            }),
            getContractsCount: injectCountEndPoint(builder, '/v1/contracts', 'ContractsCount'),
            getContractTemplatesWithFilters: filteredAndSortedQuery<ContractTemplateDto>(
                builder,
                '/v1/contracts/templates',
                {
                    pageSize: 1000,
                }
            ),
            // contract save can create/archive notifications server-side (saving an AML/UBO
            // form re-evaluates the parties' AML case block warnings)
            addContract: builder.mutation<Contract, ContractCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/contracts`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['ContractsListItem', 'Notifications'],
            }),
            updateContract: builder.mutation<Contract, ContractUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['ContractsListItem', 'Notifications'],
            }),
            deleteContract: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/contracts/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['ContractsListItem', 'Notifications'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetContractByIdQuery,
    useLazyGetContractsListQuery,
    useLazyGetContractsWithFiltersQuery,
    useGetContractsCountQuery,
    useLazyGetContractsCountQuery,
    useGetContractTemplatesWithFiltersQuery,
    useAddContractMutation,
    useUpdateContractMutation,
    useDeleteContractMutation,
} = APIContractsSlice;
